import { CommonModule, isPlatformServer } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	PLATFORM_ID,
	signal,
	ViewEncapsulation,
} from '@angular/core';
import { RouterModule } from '@angular/router';

import { AngularSvgIconModule } from 'angular-svg-icon';
import { MarkdownModule } from 'ngx-markdown';

import { ImageComponent } from '@valk-nx/components/ui-image/src/lib/image.component';
import { Sizes } from '@valk-nx/components/ui-image/src/lib/image.interface';
import { LinkComponent } from '@valk-nx/components/ui-link/src/lib/link';
import { VideoComponent } from '@valk-nx/components/ui-video/src/lib/video';
import { cn, LinkTarget } from '@valk-nx/core/lib/core';
import { HeadingDirective } from '@valk-nx/core/lib/directives/heading/heading.directive';
import { HeadingType } from '@valk-nx/core/lib/directives/heading/heading.type';
import { LazyImageLoadingDirective } from '@valk-nx/core/lib/directives/lazy-image/lazy-image.directive';
import { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { ImageQueryPipe } from '@valk-nx/core/lib/pipes/image-query/image-query.pipe';

import { ContentMargin } from './content-margin';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	selector: 'vp-hero-item',
	templateUrl: './hero-item.html',
	imports: [
		AngularSvgIconModule,
		CommonModule,
		HeadingDirective,
		ImageComponent,
		ImageQueryPipe,
		LinkComponent,
		LazyImageLoadingDirective,
		MarkdownModule,
		RouterModule,
		VideoComponent,
	],
})
export class HeroItem implements AfterViewInit {
	readonly platformId = inject(PLATFORM_ID);

	alignment = input<'left' | 'center'>('left');
	heading = input<string>();
	overline = input<string>();
	image = input<PhotoInterface>();
	sticker = input<{ src: string; altText: string }>();
	video = input<{
		vimeoId: number;
		showControls: boolean;
	}>();

	link = input<{
		content: string;
		url: string;
		title?: string;
		target?: LinkTarget;
	}>();

	headingType = input<HeadingType | undefined>(undefined);

	i = input<number>(0);

	contentMargin = input<ContentMargin>('none');
	useEagerLoading = input<boolean>(true);
	cn = cn;

	size = input<'small' | 'large'>('large');

	sizes = Sizes;

	isImageLoaded = signal<boolean>(false);
	isCustomImage = signal<boolean>(true);
	imageError = signal<boolean>(false);
	isSSRMode = signal<boolean>(false);

	heroImage = computed(() => {
		if (this.imageError()) {
			return {
				...this.image(),
				src: '/assets/images/application-icons/icon-transparent-96x96.png',
			};
		}
		return this.image();
	});

	ngAfterViewInit() {
		this.isSSRMode.set(isPlatformServer(this.platformId));
	}

	onImageLoad() {
		this.isImageLoaded.set(true);
	}

	setImageFallback() {
		this.imageError.set(true);
		this.isCustomImage.set(false);
	}
}
